// @ts-strict-ignore
import { Portal, PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  inject,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Event, NavigationEnd, Router, RouterEvent, RouterModule } from '@angular/router';
import { DevToolsComponent } from '@app/_shared/components/dev-tools/dev-tools.component';
import { SidebarMenuComponent } from '@app/_shared/components/sidebar-menu/sidebar-menu.component';
import { SbNavigateIfInactiveDirective } from '@app/_shared/directives/nav-link-if-inactive.directive';
import { RecreateOnChangeDirective } from '@app/_shared/directives/recreate-on-change.directive';
import { GlobalSlideInService } from '@app/_shared/global-slide-in/services/global-slide-in.service';
import { OverlayManagerService } from '@app/_shared/service/overlay-manager.service';
import { AccountingGroup, RouteGroup, RouteMappingService } from '@app/_shared/service/route-mapping.service';
import { ThemeService } from '@app/_shared/service/theme.service';
import { getDefaultStore } from '@app/main-store/bootstrap.selector';
import { companyFeatureFlags } from '@app/main-store/feature-flags.selectors';
import {
  hasDefaultStoreShopifySalesChannel,
  hasShopifySalesChannel,
} from '@app/main-store/integrations/integrations.selectors';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { TabsModule } from 'primeng/tabs';
import { TieredMenu } from 'primeng/tieredmenu';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'sb-main-layout',
  templateUrl: './main-layout.component.html',

  imports: [
    CommonModule,
    RouterModule,
    SidebarMenuComponent,
    RecreateOnChangeDirective,
    TranslocoModule,
    PortalModule,
    TabsModule,
    DrawerModule,
    ButtonModule,
    TieredMenu,
    FontAwesomeModule,
    DevToolsComponent,
    ScrollingModule,
    SbNavigateIfInactiveDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col bg-surface-50 dark:bg-surface-950 h-screen w-full',
    style: '--sb-mobile-header-height: 65px',
  },
})
export class MainLayoutComponent implements OnInit {
  private router = inject(Router);
  private slideInService = inject(GlobalSlideInService);
  private rxStore = inject(Store);
  private destroyRef = inject(DestroyRef);
  private routeMapping = inject(RouteMappingService);
  private overlayService = inject(OverlayManagerService);
  private themeService = inject(ThemeService);

  public activeRoute = signal<string>('');
  public activeRouteGroup = signal<RouteGroup>('accounting');
  public activeAccountingGroup = signal<AccountingGroup>('orders');

  public slideInPortal = signal<Portal<any> | null>(null);
  public slideInWidth = signal<string>('30rem');
  public slideInBackdrop = signal<boolean>(false);
  public slideInAutoFocus = signal<boolean>(false);
  public sidebarVisible = model<boolean>(false);
  public mobileMenuVisible = signal(false);
  public isMobileDevice = signal(window.innerWidth < 768);

  public slideInDrawerStyle = computed(() => ({
    width: this.slideInWidth(),
    'max-width': '100vw',
    'box-shadow': 'var(--sb-slide-in-shadow)',
    ...(this.isMobileDevice()
      ? {
          top: 'var(--sb-mobile-header-height)',
          height: 'calc(100vh - var(--sb-mobile-header-height))',
        }
      : {}),
  }));
  public activeTabMenuItem: MenuItem;
  public featureFlags = this.rxStore.selectSignal(companyFeatureFlags);
  public desktopMenuCollapsed = signal<boolean>(false);
  public hasShopifySalesChannel = this.rxStore.selectSignal(hasShopifySalesChannel);
  public hasDefaultStoreShopifySalesChannel = this.rxStore.selectSignal(hasDefaultStoreShopifySalesChannel);
  public defaultStore = this.rxStore.selectSignal(getDefaultStore);

  readonly isDarkMode = this.themeService.isDarkModeSignal;

  getTabMenuItems = computed<MenuItem[]>(() =>
    this.routeMapping.getTabGroupItems(this.activeAccountingGroup()).map((item) => ({
      label: item.label,
      routerLink: [item.route],
      command: () => this.setActiveRoute(item.route),
      requiresShopify: item.requiresShopify,
      active: () => this.isActiveRoute(item.routerLink[0]),
    })),
  );

  getGatewayMenuItems = computed<MenuItem[]>(() =>
    this.routeMapping.getGatewayTabGroupItems(this.activeAccountingGroup()).map((item) => ({
      label: item.label,
      routerLink: [item.route],
      command: () => this.setActiveRoute(item.route),
      requiresShopify: item.requiresShopify,
      styleClass: this.isActiveRoute(item.route) ? 'text-p-color-primary!' : '',
    })),
  );

  getActiveTabMenuItem = computed<MenuItem | undefined>(() =>
    this.getTabMenuItems().find((item) => this.isActiveRoute(item.routerLink[0])),
  );

  getActiveGatewayMenuItem = computed<MenuItem | undefined>(() =>
    this.getGatewayMenuItems().find((item) => this.isActiveRoute(item.routerLink[0])),
  );

  ngOnInit() {
    const savedDesktopMenuState = localStorage.getItem('sb-desktop-menu-collapsed');
    if (savedDesktopMenuState !== null) {
      this.desktopMenuCollapsed.set(savedDesktopMenuState === 'true');
    } else {
      this.desktopMenuCollapsed.set(window.innerWidth < 1280);
    }

    this.setupSlideInService();

    this.activeRoute.set(this.routeMapping.removeQueryParameters(this.router.url));
    this.updateActiveGroups(this.router.url);

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event: Event | RouterEvent) => event instanceof NavigationEnd),
      )
      .subscribe((event: NavigationEnd) => {
        this.activeRoute.set(this.routeMapping.removeQueryParameters(event.urlAfterRedirects));
        this.updateActiveGroups(event.urlAfterRedirects);
      });
  }

  public onCloseSlideIn() {
    this.slideInService.close();
  }

  public toggleDesktopMenu() {
    const newState = !this.desktopMenuCollapsed();
    this.desktopMenuCollapsed.set(newState);
    localStorage.setItem('sb-desktop-menu-collapsed', newState.toString());
  }

  public setActiveRoute(route: string) {
    this.activeRoute.set(route);
  }

  public isActiveRoute(route: string) {
    return this.activeRoute() === route;
  }

  private setupSlideInService() {
    this.slideInService.currentPortal$
      .pipe(
        filter(({ portal }) => portal !== null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ portal, width, backdrop, autoFocus }) => {
        this.slideInPortal.set(portal);
        this.slideInWidth.set(width ?? '30rem');
        this.slideInBackdrop.set(backdrop);
        this.slideInAutoFocus.set(autoFocus);
        this.sidebarVisible.set(true);
      });

    this.slideInService.close$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.sidebarVisible.set(false);
    });
  }

  private updateActiveGroups(url: string) {
    const routeGroup = this.routeMapping.getRouteGroup(url);
    this.activeRouteGroup.set(routeGroup);

    if (routeGroup === 'accounting' || routeGroup === 'crm' || routeGroup === 'invoicing') {
      const accountingGroup = this.routeMapping.getAccountingGroup(url);
      this.activeAccountingGroup.set(accountingGroup);
    }
  }

  toggleMenu(menu: TieredMenu, event: MouseEvent) {
    this.overlayService.toggle(menu, event);
  }

  private lastInnerWidth = window.innerWidth;

  @HostListener('window:resize')
  onResize() {
    const currentWidth = window.innerWidth;

    if (currentWidth >= 768 && this.mobileMenuVisible()) {
      this.mobileMenuVisible.set(false);
    }

    this.isMobileDevice.set(currentWidth < 768);

    if (this.lastInnerWidth < 1280 && currentWidth >= 1280) {
      this.desktopMenuCollapsed.set(false);
      localStorage.setItem('sb-desktop-menu-collapsed', 'false');
    } else if (this.lastInnerWidth >= 1280 && currentWidth < 1280) {
      this.desktopMenuCollapsed.set(true);
      localStorage.setItem('sb-desktop-menu-collapsed', 'true');
    }

    this.lastInnerWidth = currentWidth;
  }
}
